<template>
    <div class="area-form" v-loading="loading">

        <PageTitle
            :text="capitalizeFirstLetter(pageTitle)"
        >
        </PageTitle>

        <div class="padding">

            <div style="padding-bottom: 20px">
                <p>{{ pageDescription }}</p>
            </div>

            <div
                class="form-container padding shadow"
                style="padding-top:40px; margin-bottom:20px;">
                <ns-loader
                    :fetch-timer="null"
                    :to-load="toLoad"
                    :on-to-load-change-options="{triggerRefresh: true}"
                    @dataFetched="onDataFetched"
                    ref="areaFormLoader"
                >
                    <template slot="content">
                        <el-form
                            ref="areaForm"
                            :model="form"
                            :rules="rules"
                            label-position="top"
                            label-width="280px"
                            class="site-form"
                        >
                            <div class="form-part">
                                <div>
                                    <h2 class="step-title">Déclaration {{ commonTypeVerbose.article }} {{ areaTypeVerbose}}</h2>
                                </div>
                                <div
                                    class="part"
                                >
                                    <el-form-item
                                        label="Nom"
                                        prop="name"
                                    >
                                        <el-input
                                            v-model="form.name"
                                            style="width:100%">
                                        </el-input>
                                    </el-form-item>
        
                                    <el-form-item
                                        label="Description"
                                        prop="description"
                                    >
                                        <el-input
                                            v-model="form.description"
                                            style="width:100%">
                                        </el-input>
                                    </el-form-item>
                                </div>
                                <div class="part">
                                    <el-form-item
                                        label="Installation d’un compteur énergie"
                                        prop="energy_meter"
                                    >
                                        <el-select
                                            v-model="form.energy_meter"
                                            style="width:100%"
                                            @change="resetEnergyMeterRelatedFields"
                                        >
                                            <el-tooltip
                                                v-for="meter in energyMetersArea"
                                                :key="meter.key"
                                                :content="siblings.length === 0 ? `Un compteur virtuel ne peut être utilisé sans au moins ${commonTypeVerbose.article2} ${areaTypeVerbose} au même niveau` : ''"
                                                :disabled="siblings.length > 0 || meter.key !== 'virtual_meter'"
                                                placement="top"
                                                effect="light"
                                            >
                                                <el-option
                                                    :label="meter.value"
                                                    :value="meter.key"
                                                    :disabled="meter.key === 'virtual_meter' && siblings.length === 0"
                                                    >
                                                </el-option>
                                            </el-tooltip>
                                        </el-select>
                                    </el-form-item>
                                    <el-form-item
                                        label="Numéro du compteur"
                                        prop="energy_meter_name"
                                    >
                                        <el-input
                                            v-model="form.energy_meter_name"
                                            style="width:100%"
                                        >
                                            <template slot="prepend">{{ energyMetersPrefix(form.energy_meter) }}</template>
                                        </el-input>
                                    </el-form-item>
                                    <el-form-item
                                        label="Règle du jeu"
                                        prop="rules"
                                    >
                                        <template #label>
                                            <span>
                                                Règle du jeu
                                                <el-tooltip
                                                    :content="'Les règles du jeu sont les règles de fonctionnement des compteurs. \r Elles sont définies par le constructeur du compteur.'"
                                                    placement="top"
                                                    effect="light"
                                                >
                                                    <div slot="content">
                                                       La règle ne doit s'exprimer qu'avec le nom des compteurs du parent <br>
                                                       et des {{ areaTypeVerbose }}s du même niveau hiérarchique que {{ areaTypeVerbose === 'atelier' ? 'l\'atelier' : 'la zone' }} actuel{{ areaTypeVerbose === 'atelier' ? '' : 'le' }} et <br> 
                                                        suivre un de ces formats : <br>
                                                        <br>
                                                       <ul>
                                                        <li>parent - {{areaTypeVerbose}}1 - {{areaTypeVerbose}}2</li>
                                                        <li>parent + ({{areaTypeVerbose}}1 - {{areaTypeVerbose}}2)</li>
                                                       </ul>
                                                    </div>
                                                    <i class="el-icon-info"></i>
                                                </el-tooltip>
                                            </span>
                                        </template>
                                        <el-input
                                            v-model="form.rules"
                                            :disabled="!usesVirtualEnergyMeter"
                                            style="width:100%">
                                        </el-input>
                                    </el-form-item>
                                </div>
                            </div>
                            <div class="form-part">
                                <div>
                                    <h2 class="step-title">Données de fonctionnement</h2>
                                </div>
                                <div
                                    class="part bottom-part"
                                >
                                    <div class="part">
                                        
                                        <el-form-item
                                            label="Tension (V)"
                                            prop="tension"
                                        >
                                            <el-input-number
                                                v-model="form.tension"
                                                :disabled="usesVirtualEnergyMeter || usesWattMeter"
                                                :controls="false"
                                                :min="0"
                                                style="width:100%">
                                            </el-input-number>
                                        </el-form-item>
                                        <el-form-item
                                            label="Cosphi"
                                            prop="cos_phi"
                                        >
                                            <el-input-number
                                                v-model="form.cos_phi"
                                                :disabled="usesVirtualEnergyMeter || usesWattMeter"
                                                :controls="false"
                                                :precision="2" 
                                                :min="0"
                                                style="width:100%">
                                            </el-input-number>
                                        </el-form-item>
                                        <el-form-item
                                            label="Valeur d’équilibrage deuxième phase (%)"
                                            prop="second_phase_balancing_value"
                                        >
                                            <el-input-number
                                                v-model.number="form.second_phase_balancing_value"
                                                :disabled="usesVirtualEnergyMeter || usesWattMeter"
                                                :controls="false"
                                                :precision="2" 
                                                :min="0"
                                                style="width:100%">
                                            </el-input-number>
                                        </el-form-item>
                                        <el-form-item
                                            label="Valeur d’équilibrage troisième phase (%)"
                                            prop="third_phase_balancing_value"
                                        >
                                            <el-input-number
                                                v-model="form.third_phase_balancing_value"
                                                :disabled="usesVirtualEnergyMeter || usesWattMeter"
                                                :controls="false"
                                                :precision="2" 
                                                :min="0"
                                                style="width:100%">
                                            </el-input-number>
                                        </el-form-item>
                                        <el-form-item
                                            label="Puissance supplémentaire (kW)"
                                            prop="additional_power"
                                        >
                                            <el-input-number
                                                v-model="form.additional_power"
                                                :disabled="usesVirtualEnergyMeter || usesWattMeter"
                                                :controls="false"
                                                :min="0"
                                                style="width:100%">
                                            </el-input-number>
                                        </el-form-item>
                                    </div>
                                    <div class="part sub-part">
                                        <el-form-item
                                            label="Puissance du talon (kW)"
                                            prop="baseload_power_is_known"
                                        >
                                            <el-radio-group
                                                v-model="form.baseload_power_is_known"
                                            >
                                                <el-radio
                                                    :label="false">
                                                    Inconnue (Pmin mesurée)
                                                </el-radio>
                                                <el-radio
                                                    :label="true">
                                                    Connue
                                                </el-radio>
                                            </el-radio-group>
                                        </el-form-item>
                                        <el-form-item
                                            v-if="form.baseload_power_is_known"
                                            prop="baseload_power_kw"
                                        >
                                            <el-input-number
                                                v-model="form.baseload_power_kw"
                                                :controls="false"
                                                :min="0"
                                                style="width:100%">
                                            </el-input-number>
                                        </el-form-item>
                                    </div>
                                </div>
                            </div>
                            <el-form-item class="button-container">
                                <el-button
                                    type="primary"
                                    icon="el-icon-check"
                                    @click="save">
                                    Enregistrer
                                </el-button>
                                <el-button
                                    icon="el-icon-close"
                                    @click="leaveSite">
                                    Annuler
                                </el-button>
                            </el-form-item>
                        </el-form>
                    </template>
                </ns-loader>
            </div>
        </div>

    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { capitalizeFirstLetter } from '@/components/partials/utils';
import PageTitle from '@/components/partials/ui/PageTitle';
import FormHelp from '@/components/partials/ui/FormHelp';
import NsLoader from 'fundation-app-vuejs/src/components/partials/ns-vue-loaderV2';

export default {
    name: 'AreaForm',
    components: {
        PageTitle,
        FormHelp,
        NsLoader
    },
    data () {
        let validateEnergyMeterName = (rule, value, callback)=>{
            if (value === '') {
            callback(new Error('Le numéro du compteur d\'énergie est obligatoire'));
            } else if(!Number.isInteger(parseInt(value))){
                callback(new Error('Le numéro du compteur doit être un entier'));
            } else if (
                this.currentEnergyMeterNames.includes(this.energyMeterFullName)
            ) {
                if(this.currentArea !== null && this.energyMeterFullName === this.currentArea.energy_meter_name){
                    callback()
                }else{
                    callback(new Error('Un compteur existe déjà avec ce nom'));
                }
            } else {
              callback();
            }
        };
        let validateTension = (rules, value, callback) => {
            if(this.usesVirtualEnergyMeter || this.usesWattMeter){
                callback();
                return;
            }
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if (value < 200 || value > 1000) {
                callback(new Error('La tension doit être comprise entre 200 et 1000 V'));
            } else {
                callback();
            }
        };
        let validateCosPhi = (rules, value, callback) => {
            if (!Number.isFinite(value)) {
                callback(new Error('La valeur doit être un nombre à virgule'));
            } else if(value < 0 || value > 1) {
                callback(new Error('Le Cosphi doit être comprise entre 0 et 1'));
            } else {
                callback();
            }
        };
        let validateBalancingValue = (rules, value, callback) => {
            if (!Number.isFinite(value)) {
                callback(new Error('La valeur doit être un nombre à virgule'));
            } else if(value < -100 || value > 100) {
                callback(new Error('La valeur doit être comprise entre -100 et 100'));
            } else {
                callback();
            }
        };
        let validateAdditionalPower = (rules, value, callback) => {
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if(value < 0 || value > 10000) {
                callback(new Error('La tension doit être comprise entre 0 et 10000 kW'));
            } else {
                callback();
            }
        };
        let validateBaseloadPowerKw = (rules, value, callback) => {
            if (!Number.isInteger(value)) {
                callback(new Error('La valeur doit être un nombre entier'));
            } else if (value < 0 || value > 10000) {
                callback(new Error('La tension doit être comprise entre 0 et 10000 kW'));
            } else {
                callback();
            }
        };
        let validateRules = (rules, value, callback) => {
            if(!this.usesVirtualEnergyMeter){
                callback();
                return;
            }
            if (typeof value !== 'string') {
                callback(new Error('Règle manquante'));
                return;
            }
            const trimmedValue = value.trim();
            const hasVirtualMeter = /\bV\d+\b/.test(trimmedValue);
            if (hasVirtualMeter) {
                callback(new Error('Impossible d\'utiliser un autre compteur virtuel dans la règle'));
                return;
            }
            const regex =
            /^(?:A|WVAR|W|E|V)\d+\s*-\s*(?:(?:A|WVAR|W|E|V)\d+(?:\s*-\s*(?:A|WVAR|W|E|V)\d+)*|\((?:A|WVAR|W|E|V)\d+(?:\s*\+\s*(?:A|WVAR|W|E|V)\d+)*\))$/;
            if (!regex.test(trimmedValue)) {
                callback(new Error('Règle invalide'));
                return;
            }

            // Extra validation: first term must match parent's energy meter name
            const firstTermMatch = trimmedValue.match(/^((?:A|WVAR|W|E|V)\d+)/);
            if (firstTermMatch && this.parent && this.parent.energy_meter_name) {
                const firstTerm = firstTermMatch[1];
                if (firstTerm !== this.parent.energy_meter_name) {
                    callback(new Error('Le premier terme doit être le nom du compteur du parent'));
                    return;
                }
            }

            // Extra validation: every other term must be a sibling's energy meter name
            const allTerms = trimmedValue.match(/(?:A|WVAR|W|E|V)\d+/g) || [];
            const otherTerms = allTerms.slice(1); // skip the first term already validated
            if (otherTerms.length && Array.isArray(this.siblings)) {
                const siblingMeterNames = this.siblings
                    .map(s => s && s.energy_meter_name)
                    .filter(Boolean);

                const invalidOther = otherTerms.find(term => !siblingMeterNames.includes(term));
                if (invalidOther) {
                    callback(new Error('Chaque autre terme doit être le nom du compteur d\'une zone ou atelier au niveau actuel'));
                    return;
                }
            }

            callback();
        };
        return {
            loading: false,
            parent: null,
            siblings: [],
            areas: [],
            form: {
                name: '',
                description: '',
                energy_meter: '',
                energy_meter_name: '',
                rules: '',
                tension: 0,
                cos_phi: 0.0,
                baseload_power_is_known: true,
                baseload_power_kw: 0,
                second_phase_balancing_value: 0.0,
                third_phase_balancing_value: 0.0,
                additional_power: 0,
                parent_site_uid: null,
                parent_area_uid: null,
            },
            rules: {
                name: [
                    { required: true, message: 'Le nom est obligatoire', trigger: 'blur' },
                    { min: 1, max: 20, message: 'La longueur maximale est de 20 caractères', trigger: 'change' }
                ],
                description: [
                    { required: true, message: 'La description est obligatoire', trigger: 'change' },
                    { min: 1, max: 30, message: 'La longueur maximale est de 30 caractères', trigger: 'change' }
                ],
                energy_meter: [
                    { required: true, message: 'La sélection du compteur d\'énergie est obligatoire', trigger: 'change' }
                ],
                energy_meter_name: [
                    { required: true, message: 'Le numéro du compteur d\'énergie est obligatoire', trigger: 'change' },
                    { validator: validateEnergyMeterName, trigger:'blur'}
                ],
                tension: [
                    { validator: validateTension, trigger: 'change' }
                ],
                cos_phi: [
                    { validator: validateCosPhi, trigger: 'change' }
                ],
                second_phase_balancing_value: [
                    { validator: validateBalancingValue, trigger: 'change' }
                ],
                third_phase_balancing_value: [
                    { validator: validateBalancingValue, trigger: 'change' }
                ],
                additional_power: [
                    { validator: validateAdditionalPower, trigger: 'change' }
                ],
                baseload_power_kw: [
                    { validator: validateBaseloadPowerKw, trigger: 'change' }
                ],
                rules: [
                    { validator: validateRules, trigger: 'change' }
                ]
            },
        };
    },
    computed: {
        ...mapGetters([
            'currentSite',
            'currentArea',
            'userIsAtLeastManager',
            'energyMetersArea',
            'energyMetersPrefix',
            'currentEnergyMeterNames'
        ]),
        toLoad(){
            return [
                {
                    name: 'areas',
                    model: 'area',
                    filters: {
                        parent_site_uid: this.currentSite.uid
                    }
                },

            ];
        },
        energyMeterFullName(){
            return this.energyMetersPrefix(this.form.energy_meter) + this.form.energy_meter_name
        },
        areaType(){
            return this.$route.params.areaType || 'workshop';
        },
        parentAreaUid() {
            return this.$route.params.parentAreaUid || null;
        },
        areaTypeVerbose () {
            return this.areaType === 'workshop' ? 'atelier' : 'zone';
        },
        commonTypeVerbose () {
            return {
                article1: this.areaType === 'workshop' ? ' de l\'' : 'de la',
                article2: this.areaType === 'workshop' ? 'un' : 'une',
                new: this.areaType === 'workshop' ? ' nouvel' : 'nouvelle',
                success: this.areaType === 'workshop' ? 'Atelier enregistré avec succès' : 'Zone enregistrée avec succès'
            }
        },
        usesVirtualEnergyMeter(){
            return this.form.energy_meter === 'virtual_meter';
        },
        usesWattMeter(){
            return this.form.energy_meter.toLowerCase().includes('wattmeter');
        },
        pageTitle () {
            return this.currentArea ? this.currentArea.name : `${this.commonTypeVerbose.new} ${this.areaTypeVerbose}`;
        },
        pageDescription () {
            return this.currentArea ? `Modifer les informations de ${this.currentArea.name}` : `Créer ${this.commonTypeVerbose.article2} ${this.commonTypeVerbose.new} ${this.areaTypeVerbose}`;
        },
    },
    mounted () {
        if(!this.currentSite){
            this.leaveSite();
            return;
        }
        // this.setParent();
    },
    methods: {
        capitalizeFirstLetter,
        onDataFetched(data){
            this.$set(this, 'areas', data.areas);
            if (this.currentArea) {
                this.setForm({...this.currentArea});
                if(this.areaType === 'zone'){
                    this.setParent(this.currentArea.parent_area_uid);
                    this.setSiblings(this.currentArea.parent_area_uid, 'zone');
                }else{
                    this.parent = this.currentSite;
                    this.setSiblings(this.currentSite.uid, 'area');
                }
            }else{
                this.form.parent_site_uid = this.currentSite.uid;
                if(this.areaType === 'zone'){
                    this.setParent(this.parentAreaUid);
                    this.setSiblings(this.parentAreaUid, 'zone')
                }else{
                    this.parent = this.currentSite;
                    this.setSiblings(this.currentSite.uid, 'area');
                }
            }
        },
        setParent(uid){
            this.$store.dispatch('remoteGet', {
                url: `area/${uid}`,
            }).then(res => {
                this.$set(this, 'parent', res.data);
            }).catch(err => {
                this.$notify({
                    title: 'Erreur',
                    message: err.message,
                    type: 'error'
                });
            });
        },
        setSiblings(uid, type){
            if(type === 'zone'){
                this.$set(this, 'siblings', this.areas.filter(area => area.parent_area_uid === uid && area.uid !== this.currentArea.uid && area.archived === false));
            }else{
                this.$set(this, 'siblings', this.areas.filter(area => area.parent_area_uid === null && area.uid !== this.currentArea.uid && area.archived === false));
            }
        },
        leaveSite(){
            if(this.currentArea){
                this.$store.commit('UNSET_CURRENT_AREA');
            }
            this.$router.go(-1);
        },
        setForm(data){
            this.$set(this, 'form', data);
            this.form.parent_site_uid = this.currentSite.uid;
            this.form.energy_meter_name = this.getEnergyMeterNumber(data.energy_meter_name)
            this.form.second_phase_balancing_value = data.second_phase_balancing_value * 100;
            this.form.third_phase_balancing_value = data.third_phase_balancing_value * 100;
        },
        getEnergyMeterNumber(energyMeterName) {
            const match = energyMeterName.match(/\d+$/);
            return match ? parseInt(match[0]) : null;
        },
        resetEnergyMeterRelatedFields(value){
            if(value === 'virtual_meter' || value.includes('wattmeter')){
                this.form.rules = '';
                this.form.tension = 0;
                this.form.cos_phi = 0.0;
                this.form.second_phase_balancing_value = 0.0;
                this.form.third_phase_balancing_value = 0.0;
                this.form.additional_power = 0;
                this.form.baseload_power_is_known = true;
                this.form.baseload_power_kw = 0;
            }else{
                this.form.rules = '';
            }
        },
        async save () {
            this.$refs.areaForm.validate( valid => {
                if (valid) {
                    this.$set(this, 'loading', true);
                    // save in concrete
                    let action = this.currentArea ? 'patchItem' : 'createItem';

                    let dataToPost = { ...this.form };
                    if (this.currentArea) {
                        dataToPost.uid = this.currentArea.uid;
                        dataToPost.url = this.currentArea.url;
                    }else{
                        dataToPost.parent_area_uid = this.parentAreaUid
                    }
                    dataToPost.energy_meter_name = this.energyMeterFullName;
                    dataToPost.second_phase_balancing_value = dataToPost.second_phase_balancing_value / 100;
                    dataToPost.third_phase_balancing_value = dataToPost.third_phase_balancing_value / 100;
                    let declarationUpdated = false;
                    if (action === 'patchItem' && this.currentArea) {
                        const fields = this.$store.state.areaDeclarationFields || [];
                        declarationUpdated = fields.some(field => this.currentArea[field] !== dataToPost[field]);
                    }
                    this.$store.dispatch(action, {
                        item: dataToPost,
                        model: 'area',
                        url: 'area/'
                    // eslint-disable-next-line
                    }).then(async (res) => {
                        if (action === 'patchItem') {
                            await this.$store.dispatch('resetSimulationData', {
                                item: dataToPost,
                                itemType: 'area',
                                declarationUpdated: declarationUpdated
                            });
                        }
                        this.$notify({
                            title: 'Enregistré',
                            message: 'Site enregistré avec succès',
                            type: 'success'
                        });
                        this.$set(this, 'loading', false);
                        this.leaveSite();
                    }).catch(err => {
                        this.$set(this, 'loading', false);
                        this.$notify({
                            title: 'Erreur',
                            message: err.message,
                            type: 'error'
                        });
                    });
                } else {
                    this.$set(this, 'loading', false);
                    this.$notify({
                        title: 'Erreur',
                        message: 'Merci de vérifiez que les champs sont bien renseignés',
                        type: 'error'
                    });
                }
            });
        }
    }
};
</script>

<style lang="scss">
.area-form {
    .form-container{
        .el-form.site-form {
            display: flex;
            flex-wrap: wrap;
            flex-direction: column;;
            gap: 3rem;
            .form-part {
                flex-basis: calc(50% - 1.5rem);
                .part{
                    display: flex;
                    justify-content: space-evenly;
                    flex-wrap: wrap;
                }
                .bottom-part{
                    flex-direction: column;
                    .sub-part {
                        align-items: flex-end;
                        justify-content: center;
                        gap: 3rem;
                    }
                }
            }
        }
        .button-container{
            flex-grow: 1;
            display: flex;
            justify-content: flex-end;
        }
    }
    
}

.area-form-number-label {
    margin: 0;
    padding: 0;
}
.area-form-number-input{
    margin-top:  1em;
}
.area-form-help {
    margin-top: 1em;
}
</style>



// WEBPACK FOOTER //
// src/components/page/Synoptic/Areas/AreaForm.vue